<template>
  <div>
    <h3 v-if="title">{{ title }}</h3>

    <ul class="nav nav-tabs nav-tabs-custom mb-4" role="tablist">
      <li class="nav-item">
        <a
            class="nav-link"
            :class="{ active: activeTab === 'excel' }"
            @click.prevent="setActiveTab('excel')"
            href="#"
            role="tab"
        >
          <i class="bx bx-spreadsheet me-1"></i> Import z Excela
        </a>
      </li>
      <li class="nav-item">
        <a
            class="nav-link"
            :class="{ active: activeTab === 'source' }"
            @click.prevent="setActiveTab('source')"
            href="#"
            role="tab"
        >
          <i class="bx bx-building-house me-1"></i> Import z danych aptek
        </a>
      </li>
    </ul>

    <div class="tab-content p-3 text-muted">
      <div
          class="tab-pane"
          :class="{ active: activeTab === 'excel' }"
          id="excel-import"
          role="tabpanel"
          v-show="activeTab === 'excel'"
      >
        <CommonTableImportForm
            title="Import dostawców z Excela"
            importType="stock-suppliers"
            mode="submit"
            :fieldDefinitions="supplierImportFields"
            @onSaved="handleExcelImportSuccess"
        />
      </div>

      <div
          class="tab-pane"
          :class="{ active: activeTab === 'source' }"
          id="source-import"
          role="tabpanel"
          v-show="activeTab === 'source'"
      >
        <div class="row">
          <div class="col-md-6 col-lg-4">
            <div class="mb-3">
              <label for="sourcePharmacyId" class="form-label font-size-14 text-dark fw-bold">
                ID Źródłowej Apteki
              </label>
              <div class="input-group has-validation">
                <span class="input-group-text"><i class="bx bx-id-card"></i></span>
                <input
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': sourcePharmacyIdError }"
                    id="sourcePharmacyId"
                    v-model="sourcePharmacyId"
                    @input="validateSourcePharmacyId"
                    placeholder="Np. 123456"
                    maxlength="6"
                />
                <div v-if="sourcePharmacyIdError" class="invalid-feedback">
                  {{ sourcePharmacyIdError }}
                </div>
              </div>
              <small class="text-muted">
                Wprowadź identyfikator składający się maksymalnie z 6 cyfr.
              </small>
            </div>
          </div>
        </div>

        <div class="mt-3">
          <button
              class="btn btn-info text-white"
              @click="saveDataSource"
              :disabled="!!sourcePharmacyIdError || !sourcePharmacyId"
          >
            Importuj dane
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import CommonTableImportForm from "@/components/widgets/CommonTableImportForm.vue";

import { apiRequestsHelper } from '@/state/pinia/apiRequestHelper';
import Swal from 'sweetalert2';
import { useSimplepharmAuthModule } from "@/state/pinia/simplepharmAuthModule";
import { useCommonAppModule } from "@/state/pinia/commonAppModule";
import { useLazyLoaderModule } from "@/state/pinia/lazyloaderManagerModule";

/**
 * Komponent asystenta importu drzewa kategorii.
 * Obsługuje dwa tryby: import z pliku Excel oraz import na podstawie ID źródłowego.
 */
export default defineComponent({
  name: 'SuppliersImportAssistant',
  components: { CommonTableImportForm },
  props: {
    title: String,
  },
  emits: ['onImportSuccess'],
  data() {
    return {
      // Oryginalne dane
      baseFormData: null,
      mappingData: null,
      importFile: null,
      preferredIdentifier: null,

      // Nowe dane do obsługi zakładek i walidacji
      activeTab: 'source', // 'excel' | 'source'
      sourcePharmacyId: '',
      sourcePharmacyIdError: null,
      supplierImportFields: [
        {key: "id", label: "ID", required: false, type: "number"},
        {key: "guid", label: "GUID", required: false, type: "string"},
        {key: "name", label: "Nazwa", required: true, type: "string"},
        {key: "address", label: "Adres", required: false, type: "string"},
        {key: "city", label: "Miasto", required: false, type: "string"},
        {key: "zipcode", label: "Kod pocztowy", required: false, type: "string"},
        {key: "bankAccount", label: "Numer konta bankowego", required: false, type: "string"},
        {key: "bankName", label: "Nazwa banku", required: false, type: "string"},
        {key: "nip", label: "NIP", required: true, type: "string"},
        {key: "regon", label: "REGON", required: false, type: "string"},
        {key: "phone", label: "Telefon", required: false, type: "string"},
        {key: "email", label: "E-mail", required: false, type: "string"},
        {key: "concestion", label: "Koncesja", required: false, type: "string"},
        {key: "csiozNumber", label: "Numer CSIOZ", required: false, type: "string"},
        {key: "centralId", label: "ID centralne", required: false, type: "string"},
        {key: "isOwnSupplier", label: "Własny dostawca", required: false, type: "boolean"},
        {key: "centralCartReference", label: "Referencja koszyka centralnego", required: false, type: "string"},
      ],
    };
  },
  computed:{
    simplepharmAuth() {
      return useSimplepharmAuthModule();
    },
    commonAppModule() {
      return useCommonAppModule();
    },
    lazyloader() {
      return useLazyLoaderModule();
    },
  },
  methods: {
    /**
     * Zmienia aktywną zakładkę.
     * @param {string} tabName - Nazwa zakładki ('excel' lub 'source')
     */
    setActiveTab(tabName) {
      this.activeTab = tabName;
    },

    handleExcelImportSuccess() {
      this.$emit('onImportSuccess', 'true');
    },

    /**
     * Waliduje ID źródłowej apteki.
     * Zasady: Tylko cyfry, max 6 znaków.
     */
    validateSourcePharmacyId() {
      const value = this.sourcePharmacyId;

      // Sprawdź czy to same cyfry
      const isNumeric = /^\d*$/.test(value);

      if (!isNumeric) {
        this.sourcePharmacyIdError = 'Pole może zawierać tylko cyfry.';
        // Opcjonalnie: wyczyść znaki niebędące cyframi
        this.sourcePharmacyId = value.replace(/\D/g, '');
        return false;
      }

      if (value.length > 6) {
        this.sourcePharmacyIdError = 'Maksymalna długość to 6 znaków.';
        return false;
      }

      this.sourcePharmacyIdError = null;
      return true;
    },

    /**
     * Obsługa importu z danych źródłowych (Nowa metoda).
     * Symuluje walidację i wysłanie danych.
     */
    saveDataSource() {
      if (!this.validateSourcePharmacyId() || !this.sourcePharmacyId) {
        Swal.fire({
          title: 'Błąd walidacji',
          text: 'Popraw ID apteki źródłowej.',
          icon: 'warning',
          customClass: { container: 'swal-highest' },
        });
        return;
      }


      Swal.fire({
        title: 'Uwaga',
        text: 'Import danych z apteki źródłowej może potrwać nawet do kilku minut. Czy na pewno chcesz kontynuować?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Tak, kontynuuj',
        cancelButtonText: 'Nie, anuluj',
        customClass: { container: 'swal-highest' },
      }).then((result) => {
        if (!result.isConfirmed) {
          return;
        }
        this.lazyloader.setDisplayLazyLoader(true);
        console.log('Rozpoczynam import ze źródła dla ID apteki:', this.sourcePharmacyId);
        this.commonAppModule.httpImportSuppliersFromSourceDrugstore({ drugstoreClientId: this.sourcePharmacyId}).then(response => {

          this.$emit('onImportSuccess', 'true');
        });
      })


    },

  },
});
</script>

<style lang="scss" scoped>
/* Dodatkowe style dla zakładek, aby wyglądały spójnie z resztą aplikacji */
.nav-tabs-custom {
  border-bottom: 2px solid #eff2f7;

  .nav-item .nav-link {
    border: none;
    font-weight: 500;
    color: #495057;

    &.active {
      color: #556ee6; // Kolor główny (można podmienić na zmienną SCSS)
      background-color: transparent;
      border-bottom: 2px solid #556ee6;
    }

    &:hover {
      color: #556ee6;
    }
  }
}
</style>
